// === File: ./frontend/src/components/PdfEditor.js ===
// v17.3.0 – Dodano sprawdzanie personalizacji i autocentrowanie pól tekstowych.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Editor from './Editor';

export default function PdfEditor({ backendUrl }) {
  const { id, downloadKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [pdfData, setPdfData] = useState(null);
  const [fileObjId, setFileObjId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isPersonalizedProject, setIsPersonalizedProject] = useState(false);
  const [orderEmail, setOrderEmail] = useState(''); // Email klienta z zamówienia


  // Funkcja sprawdzająca, czy projekt zawiera pola z flagą personalizacji
  const checkProjectPersonalization = useCallback(async (projectId) => {
    if (!projectId) return false;
    
    console.log(`[PdfEditor] Sprawdzanie personalizacji dla projektu ID: ${projectId}`);
    try {
      const url = `${backendUrl}/admin/projects/${projectId}/history/latest`;
      console.log(`[PdfEditor] Pobieranie historii projektu: GET => ${url}`);
      
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        console.error(`[PdfEditor] Błąd pobierania historii projektu (status=${res.status})`);
        return false;
      }
      
      const data = await res.json();
      if (!data.jsonData || !data.jsonData.pages) {
        console.log('[PdfEditor] Brak danych jsonData lub pages w historii projektu');
        return false;
      }
      
      // Sprawdź, czy którakolwiek strona zawiera obiekt z flagą personalize
      const hasPersonalization = data.jsonData.pages.some(page => {
        const hasPersonalizeFlag = 
          (page.texts || []).some(t => t.flags?.personalize) ||
          (page.images || []).some(im => im.flags?.personalize) ||
          (page.svgs || []).some(sv => sv.flags?.personalize);
        
        return hasPersonalizeFlag;
      });
      
      console.log(`[PdfEditor] Projekt ${projectId} ${hasPersonalization ? 'zawiera' : 'nie zawiera'} pola z flagą personalizacji`);
      setIsPersonalizedProject(hasPersonalization);
      return hasPersonalization;
    } catch (error) {
      console.error("[PdfEditor] Błąd podczas sprawdzania personalizacji:", error);
      return false;
    }
  }, [backendUrl]);

  // Efekt sprawdzający personalizację po ustawieniu fileObjId
  useEffect(() => {
    if (fileObjId && !loading) {
      checkProjectPersonalization(fileObjId);
    }
  }, [fileObjId, loading, checkProjectPersonalization]);

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      // 1) Jeśli mamy pdfData w location.state => skip fetch
      if (location.state?.pdfData && location.state?.fileObjId) {
        console.log('[PdfEditor] -> using location.state pdfData/fileObjId');
        setPdfData(location.state.pdfData);
        setFileObjId(location.state.fileObjId);
        setError('');
        setLoading(false);
        return;
      }

      // 2) Pobierz tylko dane zamówienia, aby znaleźć fileObjId (projectId)
      //    Nie pobieramy już pdfData z metadanych zamówienia.
      try {
        setLoading(true);
        setError('');
        setPdfData(null); // Upewnij się, że pdfData jest null na początku

        // Pobierz dane zamówienia, by ustalić fileObjId
        const orderUrl = `${backendUrl}/admin/orders/${id}`;
        console.log('[PdfEditor] Fetching order data to find projectId => GET =>', orderUrl); // Log 1
        const orderRes = await fetch(orderUrl, { credentials: 'include' });
        console.log('[PdfEditor] Order fetch status:', orderRes.status); // Log 2
        if (!orderRes.ok) {
          throw new Error(`Błąd pobierania zamówienia (status=${orderRes.status})`);
        }
        const orderData = await orderRes.json();
        console.log('[PdfEditor] Order data received:', orderData); // Log 3
        if (!isMounted) return;

        // Ustaw email klienta z zamówienia dla integracji Blissaro
        if (orderData && orderData.customOrderId) {
          setOrderEmail(orderData.customOrderId);
          console.log(`[PdfEditor] Email klienta: ${orderData.customOrderId}`);
        } else if (orderData && orderData.client_email) {
          setOrderEmail(orderData.client_email);
          console.log(`[PdfEditor] Email klienta (alternatywne pole): ${orderData.client_email}`);
        }

        const fileObj = (orderData.files || []).find(f => f.downloadKey === downloadKey);
        console.log(`[PdfEditor] Searching for downloadKey "${downloadKey}" in files:`, orderData.files); // Log 4
        console.log('[PdfEditor] Found fileObj:', fileObj); // Log 5
        if (!fileObj) {
          throw new Error('Nie znaleziono pliku w orderData.files (fallback).');
        }
        console.log('[PdfEditor] Setting fileObjId:', fileObj.id); // Log 6
        setFileObjId(fileObj.id);

        setError('');
      } catch (ex) {
        console.error('[PdfEditor] => fallback error:', ex); // Log 7 (Error case)
        if (isMounted) {
          setError(ex.message);
        }
      } finally {
        console.log('[PdfEditor] useEffect finally block. isMounted:', isMounted); // Log 8
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [backendUrl, id, downloadKey, location.state]);

  if (loading) {
    return <div style={{ padding: 20 }}>Wczytywanie edytora PDF...</div>;
  }
  if (error) {
    return (
      <div style={{ padding: 20, color: 'red' }}>
        <p>ERROR: {error}</p>
        <button onClick={() => navigate(`/admin/orders/${id}`)}>Wróć</button>
      </div>
    );
  }
  // Usunięto sprawdzenie !pdfData - EditorState jest odpowiedzialny za ładowanie danych projektu.
  // Sprawdzamy tylko, czy udało się pobrać fileObjId.
  if (!fileObjId) {
    return (
      <div style={{ padding: 20 }}>
        <p>Brak wymaganego ID projektu (fileObjId).</p>
        <button onClick={() => navigate(`/admin/orders/${id}`)}>Wróć</button>
      </div>
    );
  }

  const handleGoBack = () => {
    navigate(`/admin/orders/${id}`);
  };

  return (
    <div style={{ width: '100%', height: '100vh' }}>
      <Editor
        backendUrl={backendUrl}
        pdfData={pdfData}
        projectId={fileObjId}      // numeric ID
        downloadKey={downloadKey}  // klucz
        onGoBack={handleGoBack}
        isPersonalizedProject={isPersonalizedProject} // Dodany prop do autocentrowania
        // Propsy dla integracji Blissaro (podobnie jak w ClientPdfEditor)
        publicMode={false} // Tryb admina
        orderEmail={orderEmail} // Email klienta z zamówienia
        orderPublicKey={downloadKey} // W trybie admina używamy downloadKey jako publicKey
        onEmailUpdated={setOrderEmail} // Callback do aktualizacji emaila
      />
    </div>
  );
}
